/**
 * GP-1230 — ONE way to render a patient address as a single display line.
 *
 * Why this exists: the same address was formatted in three places with two
 * different field precedences. The address list/radio label used
 * `streetAddress || addressLine1`, while the Process Billing wizard's
 * `handleAddressSelect` used `addressLine1 || streetAddress`. On a Kroll-synced
 * patient whose `addressLine1` still held a stale street ("938 Dynes Road Unit
 * 42") while `streetAddress` held the real one ("159 Parkdale Avenue"), the
 * radio label showed Parkdale but the Shipment Notification SMS preview showed
 * Dynes Road — an address the patient does not have, in copy that gets sent to
 * them. Same for the unit: labels used `unitNumber || addressLine2`, the wizard
 * used `addressLine2 || unitNumber`.
 *
 * `streetAddress` is the validated/Google-corrected line and wins; `addressLine1`
 * is the legacy/raw import field and is only a fallback. Everything that renders
 * an address to staff or to a patient MUST go through this function.
 */

export interface FormattableAddress {
  streetAddress?: string | null;
  addressLine1?: string | null;
  unitNumber?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  province?: string | null;
  postalCode?: string | null;
}

const trimmed = (v: unknown): string =>
  typeof v === 'string' ? v.trim() : '';

/**
 * Resolve the street line of an address. `streetAddress` (validated) wins over
 * `addressLine1` (legacy/raw import). Returns '' when neither is populated.
 */
export const resolveStreetLine = (address: FormattableAddress | null | undefined): string =>
  address ? trimmed(address.streetAddress) || trimmed(address.addressLine1) : '';

/**
 * Resolve the unit line of an address. `unitNumber` wins over `addressLine2`.
 */
export const resolveUnitLine = (address: FormattableAddress | null | undefined): string =>
  address ? trimmed(address.unitNumber) || trimmed(address.addressLine2) : '';

/**
 * Format an address as "<street>, <unit>, <city>, <province> <postal>".
 *
 * Returns `null` when the address cannot be resolved to a street line — callers
 * MUST surface that explicitly (error/placeholder) rather than substituting some
 * other address. A wrong address in a Shipment Notification SMS is a
 * patient-facing delivery failure, not a cosmetic issue.
 */
export function formatPatientAddress(
  address: FormattableAddress | null | undefined,
): string | null {
  const street = resolveStreetLine(address);
  if (!street) return null;

  const provincePostal = [trimmed(address?.province), trimmed(address?.postalCode)]
    .filter(Boolean)
    .join(' ');

  return [street, resolveUnitLine(address), trimmed(address?.city), provincePostal]
    .filter(Boolean)
    .join(', ');
}

/** Text shown wherever an address has no resolvable street line. */
export const UNRESOLVABLE_ADDRESS_LABEL =
  'Address incomplete — no street address on file';

/**
 * GP-1270 — shape of `Order.deliveryAddressSnapshot` (the frozen JSONB
 * snapshot column, backend-v2/src/orders/entities/order.entity.ts). Distinct
 * from `FormattableAddress` above (the patient's saved/validated address
 * record) — different field names (`postal_code` vs `postalCode`, `street`
 * vs `streetAddress`), so it gets its own formatter rather than being forced
 * through `formatPatientAddress`.
 */
export interface DeliveryAddressSnapshot {
  street?: string;
  unit?: string;
  city?: string;
  province?: string;
  postal_code?: string;
}

/**
 * Format a deliveryAddressSnapshot (or clinic variant) into a display string.
 * Returns '' when no snapshot is available — callers MUST render an explicit
 * "no address on file" marker instead of the empty string, per the repo's
 * no-silent-fallback rule (a blank string reads as "no data" to a human but
 * types identically to a real address to everything downstream).
 */
export const formatDeliveryAddressSnapshot = (
  snapshot?: DeliveryAddressSnapshot | null,
  clinicSnapshot?: DeliveryAddressSnapshot | null,
): string => {
  const snap = clinicSnapshot || snapshot;
  if (!snap) return '';
  const parts = [
    snap.unit ? `${snap.unit} - ${snap.street}` : snap.street,
    snap.city,
    `${snap.province || ''} ${snap.postal_code || ''}`.trim(),
  ].filter(Boolean);
  return parts.join(', ');
};

/** Text shown wherever a deliveryAddressSnapshot has no resolvable address. */
export const NO_DELIVERY_ADDRESS_LABEL = 'No address on file';

export default formatPatientAddress;
